// Templates management section — rendered inside Settings.
// Features: list templates (name/phase+task counts/created); create a project
// from a template; delete with confirm. Mirrors SpacesSection/StatusesSection
// styling conventions.

import { useState } from "preact/hooks";
import { FolderPlus, Trash2 } from "lucide-preact";
import type { ProjectTemplateItem } from "@juuplan/shared";
import { toastSuccess } from "../lib/toast";
import { useTemplates, createProjectFromTemplate, removeTemplate } from "../store/templates";
import { useLocation } from "wouter-preact";
import { Modal } from "./Modal";
import { INPUT_CLS } from "./NewProjectModal";

import { Button } from "./ui/Button";

/**
 * Workspace-level templates management list.
 * Rendered inside Settings as an accordion-style section.
 */
export function TemplatesSection(): preact.JSX.Element {
  const { items, loading, error } = useTemplates();
  const [, setLocation] = useLocation();

  // Delete confirm state
  const [confirmDel, setConfirmDel] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState<Set<number>>(new Set());

  // Create-from-template dialog state
  const [fromTemplateId, setFromTemplateId] = useState<number | null>(null);

  const handleDelete = async (id: number): Promise<void> => {
    setDeleting((prev) => new Set(prev).add(id));
    try {
      await removeTemplate(id);
      setConfirmDel((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toastSuccess("Template gelöscht.");
    } catch {
      // api wrapper already toasted
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const selectedTemplate = fromTemplateId !== null
    ? items.find((t) => t.id === fromTemplateId) ?? null
    : null;

  return (
    <div class="flex flex-col gap-2">
      {loading && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      )}

      {error && (
        <p class="text-xs text-[color:var(--color-error)]">Fehler: {error}</p>
      )}

      {!loading && !error && items.length === 0 && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine Templates vorhanden. Öffne ein Projekt und speichere es als Template.
        </p>
      )}

      {!loading && items.map((t) => {
        const phaseCount = t.payload?.phases?.length ?? 0;
        const taskCount = (t.payload?.phases ?? []).reduce(
          (sum, p) => sum + (Array.isArray(p.tasks) ? p.tasks.length : 0),
          0
        );
        const isConfirmDel = confirmDel.has(t.id);
        const isDel = deleting.has(t.id);

        return (
          <div
            key={t.id}
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden"
          >
            {/* Row */}
            <div class="flex items-center gap-2 px-3 py-2">
              <span class="flex-1 min-w-0">
                <span class="block text-sm font-medium text-[color:var(--color-on-surface)] truncate">
                  {t.name}
                </span>
                <span class="block text-label text-[color:var(--color-on-surface-variant)] mt-0.5">
                  {phaseCount} Phasen · {taskCount} Aufgaben · {new Date(t.created_at).toLocaleDateString("de-DE")}
                </span>
              </span>

              <button
                type="button"
                onClick={() => setFromTemplateId(t.id)}
                title="Projekt aus Template anlegen"
                class="shrink-0 inline-flex items-center gap-1 rounded px-2 py-1 text-label text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)] transition-colors"
              >
                <FolderPlus size={14} aria-hidden="true" />
                Projekt anlegen
              </button>

              <button
                type="button"
                onClick={() =>
                  setConfirmDel((prev) => {
                    const next = new Set(prev);
                    next.add(t.id);
                    return next;
                  })
                }
                disabled={isDel}
                title="Template löschen"
                class="shrink-0 rounded p-1 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)] transition-colors"
                aria-label={`Template „${t.name}" löschen`}
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </div>

            {/* Delete confirm */}
            {isConfirmDel && (
              <div class="border-t border-[color:var(--color-outline-variant)] px-3 py-2 flex flex-col gap-1.5">
                <p class="text-label text-[color:var(--color-on-error-container)] bg-[color:var(--color-error-container)] rounded px-2 py-1.5">
                  Template „{t.name}" wirklich löschen?
                </p>
                <div class="flex items-center gap-1.5 justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.delete(t.id);
                        return next;
                      })
                    }
                    class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <Button
                    variant="destructive"
                    size="xs"
                    type="button"
                    onClick={() => void handleDelete(t.id)}
                    disabled={isDel}
                  >
                    {isDel ? "Lösche…" : "Löschen"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Create-from-template dialog */}
      {selectedTemplate !== null && (
        <CreateFromTemplateDialog
          template={selectedTemplate}
          onClose={() => setFromTemplateId(null)}
          onCreated={(id) => {
            setFromTemplateId(null);
            setLocation(`/p/${id}`);
          }}
        />
      )}
    </div>
  );
}

// ---------- Create-from-template dialog ----------

interface CreateFromTemplateDialogProps {
  template: ProjectTemplateItem;
  onClose: () => void;
  onCreated: (projectId: number) => void;
}

function CreateFromTemplateDialog(props: CreateFromTemplateDialogProps): preact.JSX.Element {
  const { template, onClose, onCreated } = props;
  const [name, setName] = useState(template.name);
  const [startDate, setStartDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const created = await createProjectFromTemplate({
        template_id: template.id,
        name: trimmed,
        start_date: startDate || undefined,
      });
      toastSuccess(`Projekt „${created.name}" aus Template angelegt.`);
      onCreated(created.id);
    } catch {
      // api wrapper already toasted
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Projekt aus „${template.name}"`} onClose={onClose} size="sm">
      <form onSubmit={onSubmit} class="flex flex-col gap-4">
        <label class="flex flex-col gap-1">
          <span class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
            Projektname <span class="text-[color:var(--color-error)]">*</span>
          </span>
          <input
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            required
            maxLength={200}
            autoFocus
            class={INPUT_CLS}
            placeholder="z.B. Kundenprojekt 2026"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
            Startdatum (optional)
          </span>
          <input
            type="date"
            value={startDate}
            onInput={(e) => setStartDate((e.target as HTMLInputElement).value)}
            class={INPUT_CLS}
          />
          <p class="text-label text-[color:var(--color-on-surface-variant)]">
            Aufgaben mit Offset werden ab diesem Datum berechnet.
          </p>
        </label>

        <div class="flex items-center justify-end gap-2 pt-2 border-t border-[color:var(--color-outline-variant)] -mx-5 -mb-4 px-5 pb-4 mt-2">
          <button
            type="button"
            onClick={onClose}
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-4 py-2 text-sm font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
          >
            Abbrechen
          </button>
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={submitting || !name.trim()}
          >
            {submitting ? "Lege an…" : "Anlegen"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
